import { z } from 'zod';
import { getBackendAppToolsExtensionsDir } from '@wix/cli-astro-definitions';
import {
  pathAvailableValidation,
  VALID_PATH_REGEX,
} from '../../../dev-center/components/validations/common.js';
import type { ExtensionDef } from '../generate-schema/types.js';
import { defineField } from '../generate-schema/definitions.js';
import { generateAppTools } from './generate.js';

const appToolConfigSchema = z.object({
  methodName: z
    .string()
    .min(1)
    .max(30)
    .describe(
      defineField({
        title: 'Method name',
        description: 'Name of the method that implements this tool.',
      })
    ),
  description: z
    .string()
    .min(10)
    .max(1000)
    .describe(
      defineField({
        title: 'Description',
        description:
          'What this tool does — used by AI agents to decide when to call it.',
      })
    ),
  requestSchema: z
    .record(z.unknown())
    .optional()
    .describe(
      defineField({
        title: 'Request schema',
        description: 'JSON Schema for the tool input parameters.',
      })
    ),
  responseSchema: z
    .record(z.unknown())
    .optional()
    .describe(
      defineField({
        title: 'Response schema',
        description: 'JSON Schema for the tool output.',
      })
    ),
  activated: z
    .boolean()
    .optional()
    .describe(
      defineField({
        title: 'Activated',
        description: 'Whether the tool is active. Defaults to false.',
      })
    ),
});

const schema = z.object({
  extensionType: z.literal('APP_TOOLS'),
  name: z
    .string()
    .regex(VALID_PATH_REGEX)
    .max(19)
    .describe(
      defineField({
        title: 'Extension name',
        description: 'Lowercase alphanumeric and hyphens only.',
      })
    ),
  tools: z
    .array(appToolConfigSchema)
    .min(1)
    .max(100)
    .optional()
    .describe(defineField({ title: 'Tools' })),
});

export const appTools: ExtensionDef<typeof schema> = {
  title: 'App Tools',
  description: 'A backend extension that provides tools to Wix AI agents',
  group: 'Backend',
  zodSchema: schema,
  validate: async (projectFolder, data) => {
    const dir = getBackendAppToolsExtensionsDir(projectFolder);
    return pathAvailableValidation(dir)(data.name)
      ? null
      : 'Extension name must be unique, please choose a name that has not been used yet.';
  },
  generate: ({ projectFolder, data }) =>
    generateAppTools(projectFolder, data.name, data.tools),
};
